import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';

const PlotlyChart = ({ traces, title, normalize = false, onRangeUpdate, startDate, endDate }) => {
  // Track both X and Y ranges to prevent re-renders from resetting them
  const [xRange, setXRange] = useState(null);
  const [yRange, setYRange] = useState(null);

  const noData = !traces.length;

  const handleRelayout = (eventData) => {
    if (!eventData) return;

    let newXRange = xRange;
    let newYRange = yRange;

    // Handle X-axis zoom or reset
    if (eventData['xaxis.range[0]'] !== undefined && eventData['xaxis.range[1]'] !== undefined) {
      newXRange = [eventData['xaxis.range[0]'], eventData['xaxis.range[1]']];
    } else if (eventData['xaxis.autorange']) {
      newXRange = null; // Double-click reset
    }

    // Handle Y-axis zoom or reset
    if (eventData['yaxis.range[0]'] !== undefined && eventData['yaxis.range[1]'] !== undefined) {
      newYRange = [eventData['yaxis.range[0]'], eventData['yaxis.range[1]']];
    } else if (eventData['yaxis.autorange']) {
      newYRange = null; // Double-click reset
    }

    setXRange(newXRange);
    setYRange(newYRange);
  };

  useEffect(() => {
    // Update the range in the parent component using the callback
    if (xRange && onRangeUpdate) {
      onRangeUpdate(xRange);
    }
  }, [xRange, onRangeUpdate]);

  const processedTraces = traces.map((trace) => {
    const ymax = Math.max(...trace.y);
    const ymin = Math.min(...trace.y);
    if (normalize) {
      return {
        ...trace,
        y: trace.y.map((ypoint) => {
          if (ymin === ymax) {
            return 100;
          } else {
            return ((ypoint - ymin) / (ymax - ymin)) * 100;
          }
        }),
      };
    } else {
      return {
        ...trace,
      };
    }
  });

  let layout = {
    title: {
      text: title,
      font: {
        family: 'Arial',
        size: 22,
      },
      xref: 'paper',
      x: 0.05,
    },
    margin: {
      l: 50,
      r: 50,
      t: 80,
      b: 80,
      pad: 4,
    },
    xaxis: {
      // Use the interactive state if it exists, otherwise fall back to the props
      range: xRange || [new Date(startDate), new Date(endDate)],
      showgrid: false,
      type: 'date',
      tickformat: '%I:%M %p \n %B %d, %Y',
    },
    // Safely apply the interactive Y-axis range if it exists
    ...(yRange && { yaxis: { range: yRange } }),
    showlegend: true,
    legend: {},
  };

  let config = {
    responsive: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['select2d', 'lasso2d', 'pan2d', 'zoom2d'],
  };

  if (noData) {
    layout.annotations = [
      {
        x: 0.5,
        y: 0.5,
        xref: 'paper',
        yref: 'paper',
        text: 'No Data',
        showarrow: false,
        font: {
          size: 44,
          family: 'Arial',
        },
        opacity: 0.7,
      },
    ];
    layout.xaxis = {
      showticklabels: false,
      showgrid: false,
      zeroline: false,
      linecolor: 'black',
      linewidth: 1,
      mirror: true,
    };
    layout.yaxis = {
      showticklabels: false,
      showgrid: false,
      zeroline: false,
      linecolor: 'black',
      linewidth: 1,
      mirror: true,
    };
  }

  return (
    <Plot
      data={processedTraces}
      layout={layout}
      config={config}
      useResizeHandler={true}
      className="w-100 h-100"
      onRelayout={handleRelayout}
    />
  );
};

export default PlotlyChart;
